/* eslint import/no-webpack-loader-syntax: off */

import React, { useState, useEffect, useRef, Fragment } from 'react';
import _ from 'lodash';
import { Helmet } from 'react-helmet';
import InfiniteScroll from 'react-infinite-scroll-component';

import Collapse from '@material-ui/core/Collapse';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';

import { useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { fetchItemsFromCategoriesData } from '../actions/items';

import ProductBox from './ProductBox';
import PreviousBar from './PreviousBar';
import Filter from './Filter';

import { formatCategoryName } from '../scripts/functions';

import { ITEM_IMG_URL } from '../config';

export default function CategoryPage() {
    const { category: slug } = useParams();

    const dispatch = useDispatch();
    const location = useLocation();

    const { currentUser } = useSelector((state) => state.auth);
    const loggedIn = !currentUser.anonymous;

    const {
        items: allItems,
        categories,
        noResults,
    } = useSelector((state) => state.items);

    const canonicalSlug = slug
        ? slug
              .replaceAll(' ', '')
              .replaceAll('/', '')
              .replaceAll('-', '')
              .replaceAll('&', '')
              .replaceAll('(', '')
              .replaceAll(')', '')
              .replaceAll(',', '')
        : '';

    const category = canonicalSlug
        ? categories.find((category) => {
              const name = category.name
                  .replaceAll(' ', '')
                  .replaceAll('/', '')
                  .replaceAll('-', '')
                  .replaceAll('&', '')
                  .replaceAll('(', '')
                  .replaceAll(')', '')
                  .replaceAll(',', '')
                  .toLowerCase();

              return name === canonicalSlug;
          })
        : null;

    const { name, id, description, content, subheader, lowest, banner } =
        category || {};

    const [items, setItems] = useState([]);
    const [searched, setSearched] = useState(false);
    const [showQty, setShowQty] = useState(25);
    const [showContent, setShowContent] = useState(false);
    const [filteredItems, setFilteredItems] = useState([]);
    const [reset, setReset] = useState(false);
    const [showItems, setShowItems] = useState(true);
    const [showLoading, setShowLoading] = useState(true);
    const [shouldReset, setShouldReset] = useState(false);

    const itemsRef = useRef();
    const loadingRef = useRef();

    useEffect(() => {
        setItems([]);
        setFilteredItems([]);
        setShouldReset(true);

        setShowItems(true);
        setSearched(false);
    }, [location]);

    useEffect(() => {
        return () => {
            clearTimeout(itemsRef.current);
            clearTimeout(loadingRef.current);
        };
    }, []);

    useEffect(() => {
        clearTimeout(itemsRef.current);
        clearTimeout(loadingRef.current);

        setShowItems(false);
        setShowLoading(false);

        itemsRef.current = setTimeout(() => setShowItems(true), 100);
        loadingRef.current = setTimeout(() => setShowLoading(true), 100);
    }, [reset]);

    useEffect(() => {
        if (allItems && allItems.length) {
            const newItems = allItems.filter((item) => {
                const matchingPath = item.path.find((p) => p.startsWith(id));

                return !!matchingPath;
            });

            if (reset) {
                setReset(false);
                setFilteredItems(newItems);
            } else {
                setItems(newItems);
                setFilteredItems(newItems);
            }
        }
    }, [allItems, reset]);

    useEffect(() => {
        if (
            !searched &&
            id &&
            (id.length === 3 || id.startsWith('000') || !items.length)
        ) {
            setSearched(true);
            dispatch(fetchItemsFromCategoriesData(id));
        }
    });

    const generateCategoryURL = (id, type, name, link) => {
        if (id === '999') {
            return '/clearance';
        }

        if (type === 'C' && name) {
            return `/category/${formatCategoryName(name)}`;
        }

        if (type === 'L' && link) {
            return link;
        }

        return '/products';
    };

    const handleCategoryClick = () => {
        setShowItems(false);
        window.scrollTo({ top: 0 });
    };

    const renderCategories = () => {
        return (
            <div
                className="matching-container-variant"
                style={{
                    display: 'grid',
                    margin: '4rem -40px',
                }}
            >
                {categories
                    .filter((c) =>
                        id
                            ? c.id.startsWith(id) &&
                              c.id.length === id.length + 4
                            : c.id.length === 3 || c.id.startsWith('000')
                    )
                    .map((c) => (
                        <ProductBox
                            key={c.id}
                            loggedIn={loggedIn}
                            dispatch={dispatch}
                            item={c}
                            type="category"
                            variant="top"
                            picture={c.image}
                            url={generateCategoryURL(
                                c.id,
                                c.type,
                                c.name,
                                c.link
                            )}
                            open={() => handleCategoryClick()}
                        />
                    ))}
            </div>
        );
    };

    const renderItems = () => {
        if (!noResults) {
            if (showItems && !_.isEmpty(filteredItems)) {
                return (
                    <InfiniteScroll
                        dataLength={
                            showQty > filteredItems.length
                                ? filteredItems.length
                                : showQty
                        } //This is important field to render the next data
                        next={() => {
                            setShowQty(showQty + 25);
                        }}
                        hasMore={showQty < filteredItems.length}
                        loader={<h4>Loading...</h4>}
                        style={{
                            display: 'grid',
                            margin: '2rem 0',
                        }}
                    >
                        {filteredItems.slice(0, showQty).map((item) => (
                            <ProductBox
                                key={item.sku}
                                loggedIn={loggedIn}
                                dispatch={dispatch}
                                item={item}
                                type="item"
                                picture={
                                    item.images
                                        ? item.images.find(
                                              (image) => image.thumbnail
                                          ).imageFile
                                        : ''
                                }
                                overlay={
                                    item.images
                                        ? item.images.find(
                                              (image) => image.thumbnail
                                          ).overlay
                                        : ''
                                }
                                url={`/item/${item.sku}`}
                                open={() => {
                                    window.scrollTo({ top: 0 });
                                }}
                            />
                        ))}
                    </InfiniteScroll>
                );
            }

            if (showLoading) {
                return (
                    <div className="loading-container">
                        <CircularProgress
                            color="primary"
                            style={{ marginRight: '0.5em' }}
                        />
                        <span>Loading...</span>
                    </div>
                );
            }
        }

        return '';
    };

    const renderContent = () => {
        const contentArray = content ? content.split('\n') : [];

        if (!_.isEmpty(contentArray)) {
            return (
                <Fragment>
                    <Collapse in={content && showContent}>
                        {contentArray.map((p, i) => (
                            <p
                                key={`page-paragraph-${i}`}
                                className="page-paragraph"
                            >
                                {p}
                            </p>
                        ))}
                    </Collapse>
                </Fragment>
            );
        }

        return '';
    };

    const previous = id
        ? categories
              .filter((c) => id.startsWith(c.id))
              .sort((a, b) => a.id - b.id)
        : [];

    return (
        <div className="page-container results-page-container products-page-container">
            <Helmet>
                <title>{`${
                    name || 'All Categories'
                } | Kenco Label & Tag`}</title>
                <meta
                    name={
                        description ||
                        name ||
                        'Shop thousands of Kenco products, including labels, tags, printers, ink, price guns, and more.'
                    }
                />
            </Helmet>
            <div className="products-page">
                {category && <PreviousBar previous={previous} />}
                <div className="brand-top-header">
                    <h1
                        className="products-header brand-products-header"
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            overflow: 'hidden',
                        }}
                    >
                        <div className="hide-mobile">
                            {banner ? (
                                <img
                                    src={`${ITEM_IMG_URL}${banner}`}
                                    alt={name}
                                    style={{ flex: 1 }}
                                />
                            ) : (
                                name || 'All Categories'
                            )}
                        </div>
                        <span className="hide-desktop">{name}</span>
                    </h1>
                </div>
                <p className="category-subheader brand-page-description">
                    {subheader ||
                        (name
                            ? ''
                            : 'Shop thousands of Kenco products, including labels, tags, printers, ink, price guns, and more.')}
                    {content && (
                        <Button
                            color="primary"
                            onClick={() => setShowContent(!showContent)}
                            startIcon={
                                showContent ? (
                                    <i className="fas fa-compress-alt"></i>
                                ) : (
                                    <i className="fas fa-book-open"></i>
                                )
                            }
                            style={{ marginLeft: '0.5rem' }}
                        >
                            {showContent ? 'Show Less' : 'Read More'}
                        </Button>
                    )}
                </p>
                {renderContent()}
                {categories && !lowest && renderCategories()}
                {items && categories && category && (
                    <>
                        <div className="item-page-section-break" />
                        <h2 className="brand-page-header">
                            {noResults
                                ? 'Coming soon! No items yet.'
                                : 'All Products'}
                        </h2>
                        <div className="hide-mobile">
                            <div className="products-filter-container">
                                <Filter
                                    items={filteredItems}
                                    setFilteredItems={setFilteredItems}
                                    setReset={setReset}
                                    hideCategories
                                    hideBrands
                                    max={items.length}
                                    shouldReset={shouldReset}
                                    setShouldReset={setShouldReset}
                                />
                                {renderItems()}
                            </div>
                        </div>
                        <div className="hide-desktop">{renderItems()}</div>
                    </>
                )}
            </div>
        </div>
    );
}
